import { useMutation, useQueryClient, type UseMutationOptions } from "@tanstack/react-query";
import { toast } from "sonner";

/**
 * Opções do `useMutationWithToast`.
 *
 * As mensagens são **opt-in**: sem `successMessage` não há toast de sucesso,
 * sem `errorMessage` não há toast de erro. É deliberado — a maioria dos
 * chamadores da app trata o resultado no próprio sítio (com o nome do registo
 * na mensagem, que o hook não conhece). Se o toast de erro fosse automático,
 * adotar este hook nesses sítios passava a mostrar dois toasts.
 */
type MutationWithToastOptions<TData, TError, TVariables, TContext> = {
  mutationFn: (variables: TVariables) => Promise<TData>;
  /** Toast de sucesso. Omitir quando quem chama já mostra o seu. */
  successMessage?: string | ((data: TData, variables: TVariables) => string);
  /**
   * Toast de erro. Omitir quando quem chama trata o erro (try/catch).
   *
   * A forma-função recebe o erro já normalizado pelo interceptor do axios
   * (`lib/api.ts`): `error.message` é a mensagem vinda do backend.
   */
  errorMessage?: string | ((error: TError, variables: TVariables) => string);
  /**
   * Query keys a invalidar depois do sucesso.
   *
   * Aceita a forma-função quando alguma key depende do resultado ou das
   * variáveis da mutation (ex.: o detalhe do registo que acabou de mudar):
   * `invalidateQueries: (_data, id) => [["items"], ["item", id]]`
   */
  invalidateQueries?:
    | Array<readonly unknown[]>
    | ((data: TData, variables: TVariables) => Array<readonly unknown[]>);
  onSuccessCallback?: (data: TData, variables: TVariables, context: TContext | undefined) => void | Promise<void>;
  onErrorCallback?: (error: TError, variables: TVariables, context: TContext | undefined) => void | Promise<void>;
  /** Escape hatch para as restantes opções do `useMutation`. */
  mutationOptions?: Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    "mutationFn" | "onSuccess" | "onError"
  >;
};

/**
 * `useMutation` + invalidação de queries + toasts opcionais.
 *
 * Poupa o `useQueryClient` e o bloco de `onSuccess` repetidos em cada mutation.
 *
 * @example
 * // Toast tratado aqui:
 * const m = useMutationWithToast({
 *   mutationFn: (data: CreateInput) => apiRequest<Item>("/items", { method: "POST", body: data }),
 *   successMessage: "Item criado.",
 *   errorMessage: "Erro ao criar o item.",
 *   invalidateQueries: [["items"]],
 * });
 *
 * @example
 * // Toast tratado por quem chama — só invalidação:
 * const m = useMutationWithToast({
 *   mutationFn: (id: string) => apiRequest<void>(`/items/${id}`, { method: "DELETE" }),
 *   invalidateQueries: [["items"]],
 * });
 *
 * @example
 * // Key dependente das variáveis:
 * const m = useMutationWithToast({
 *   mutationFn: (id: string) => apiRequest<Item>(`/items/${id}`, { method: "PATCH", body: {} }),
 *   invalidateQueries: (_data, id) => [["items"], ["item", id]],
 * });
 */
export function useMutationWithToast<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown
>(options: MutationWithToastOptions<TData, TError, TVariables, TContext>) {
  const queryClient = useQueryClient();

  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn: options.mutationFn,
    onSuccess: async (data, variables, context) => {
      if (options.invalidateQueries) {
        const keys =
          typeof options.invalidateQueries === "function"
            ? options.invalidateQueries(data, variables)
            : options.invalidateQueries;

        await Promise.all(
          keys.map((queryKey) => queryClient.invalidateQueries({ queryKey }))
        );
      }

      if (options.successMessage) {
        toast.success(
          typeof options.successMessage === "function"
            ? options.successMessage(data, variables)
            : options.successMessage
        );
      }

      await options.onSuccessCallback?.(data, variables, context);
    },
    onError: async (error, variables, context) => {
      if (options.errorMessage) {
        const message =
          typeof options.errorMessage === "function"
            ? options.errorMessage(error, variables)
            : options.errorMessage;
        // Texto vazio = quem chama vai tratar este erro à sua maneira (ex.: mostrar o que mudou num conflito).
        if (message) toast.error(message);
      }

      await options.onErrorCallback?.(error, variables, context);
    },
    ...options.mutationOptions,
  });
}
